import { useEffect, useLayoutEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { useSessionStore, type Role } from '@/stores/useSessionStore'
import { useMe } from '@/features/auth/api'
import { PLATFORM_ADMIN_FIRST_LOGIN_PATH } from '@/features/auth/lib/constants'
import {
  clearTenantHandoffStartedTimestamp,
  getPublicAuthApexHostname,
} from '@/features/auth/lib'
import {
  canResumeLastTenantFromApex,
  tryResumeLastTenantFromApex,
} from '@/features/auth/lib/last-tenant-resume'
import { TenantHostRedirect } from './TenantHostRedirect'

function AuthBootstrapSpinner() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <CircularProgress />
    </Box>
  )
}

interface Props {
  readonly allowedRoles?: Role[]
}

/**
 * Soft return to the auth apex home.
 *
 * Do NOT use `?auth_reset=1` here — that logs out and clears host-only cookies on
 * the apex. After a failed tenant-host `/me` (common right after handoff while
 * cookies settle, or when the tenant host cannot read the new jar), those apex
 * cookies from the login that produced the handoff are still valid and must be
 * kept so the session can re-bootstrap and TenantHostRedirect can retry.
 */
function RedirectToApexSoftBootstrap() {
  useLayoutEffect(() => {
    // Drop the in-flight timestamp so apex can retry; keep the attempt counter.
    clearTenantHandoffStartedTimestamp()
    const loc = globalThis.location
    const apex = getPublicAuthApexHostname(loc.hostname)
    const portSuffix = loc.port ? `:${loc.port}` : ''
    loc.replace(`${loc.protocol}//${apex}${portSuffix}/`)
  }, [])

  return <AuthBootstrapSpinner />
}

export function RequireAuth({ allowedRoles }: Props) {
  const { isAuthenticated, user, explicitLogout, clearSession } = useSessionStore()
  const location = useLocation()
  const { isLoading, isError } = useMe()

  useEffect(() => {
    if (isError) {
      clearSession()
    }
  }, [isError, clearSession])

  // Do not gate on `explicitLogout` — `/me` errors call `clearSession()`, which
  // sets that flag, but apex still needs one chance to open the last tenant host.
  // Logout clears the localStorage pointer + sets POST_LOGOUT (see canResume).
  const apexNeedsTenantResume =
    !isAuthenticated &&
    !user &&
    (isError || !isLoading) &&
    canResumeLastTenantFromApex()

  /**
   * Host-only cookies on `{tenant}.localhost` are invisible on `localhost`.
   * When apex `/me` fails (or there is no session), resume the last tenant host
   * once so the durable tenant jar can hydrate the session.
   */
  useLayoutEffect(() => {
    if (!apexNeedsTenantResume) return
    tryResumeLastTenantFromApex()
  }, [apexNeedsTenantResume])

  if (apexNeedsTenantResume) {
    return <AuthBootstrapSpinner />
  }

  if (isError) {
    const hostname =
      globalThis.window === undefined ? '' : globalThis.location.hostname
    const apex = getPublicAuthApexHostname(hostname)
    if (apex !== hostname) {
      return <RedirectToApexSoftBootstrap />
    }
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (
    !explicitLogout &&
    !isAuthenticated &&
    !user &&
    isLoading
  ) {
    return <AuthBootstrapSpinner />
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />
  }

  if (user.requiresPasswordReset ?? false) {
    return <Navigate to={PLATFORM_ADMIN_FIRST_LOGIN_PATH} replace />
  }

  return <TenantHostRedirect />
}
